import React from "react";
import { useTranslation } from "react-i18next";
import { safeHtml } from "@/lib/safeHtml";
import CatalogPreview from "@/components/CatalogPreview";
import { Check, Info } from "lucide-react";
import { STYLES } from "./constants";

/**
 * Step 2: architectural style picker + per-style scarcity warning +
 * the live "what FDP profiles will be applied" preview strip +
 * simple-user AI/manual toggle + render-mode picker (Standard / Renovation /
 * Empty room). Visually a single sticky right-rail card.
 */
export default function StyleStep({
  simType,
  style,
  onStyleChange,
  coverage,
  selectedCats,
  isSimple,
  aiPickRandom,
  onAiPickRandomChange,
  renderMode,
  onRenderModeChange,
}) {
  const { t } = useTranslation();

  const interiorModes = [
    {
      id: "STANDARD",
      label: t("simulate.render_mode_standard_int"),
      desc: t("simulate.render_mode_standard_int_desc"),
      emoji: "🏠",
    },
    {
      id: "RENOVATION",
      label: t("simulate.render_mode_renovation_int"),
      desc: t("simulate.render_mode_renovation_int_desc"),
      emoji: "✨",
    },
    {
      id: "EMPTY_ROOM",
      label: t("simulate.render_mode_empty_room"),
      desc: t("simulate.render_mode_empty_room_desc"),
      emoji: "🧹",
    },
  ];
  const exteriorModes = [
    {
      id: "STANDARD",
      label: t("simulate.render_mode_standard_ext"),
      desc: t("simulate.render_mode_standard_ext_desc"),
      emoji: "🏛️",
    },
    {
      id: "RENOVATION",
      label: t("simulate.render_mode_renovation_ext"),
      desc: t("simulate.render_mode_renovation_ext_desc"),
      emoji: "✨",
    },
  ];
  const modes = simType === "INTERIOR" ? interiorModes : exteriorModes;

  // Find the scarcity item for the picked (simType, style) — if total
  // photos for that combo is under the threshold we surface a warning so
  // the user sets expectations correctly.
  const scarcityItem =
    style && coverage
      ? (coverage.items || []).find(
          (i) => i.sim_type === simType && i.style === style,
        )
      : null;
  const styleLabel = STYLES.find((s) => s.id === style)?.label || style;

  return (
    <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-6 md:p-8 sticky top-24">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-500/15 text-orange-400 text-sm font-bold">
          2
        </div>
        <h3 className="font-outfit font-bold text-white text-xl">
          {t("simulate.step_2_title")}
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {STYLES.map((s) => (
          <button
            key={s.id}
            onClick={() => onStyleChange(s.id)}
            className={`relative h-32 rounded-lg overflow-hidden border-2 transition-all duration-300 group ${
              style === s.id
                ? "border-orange-500 ring-2 ring-orange-500/30"
                : "border-white/10 hover:border-white/30"
            }`}
            data-testid={`style-pick-${s.id.toLowerCase()}`}
          >
            <img
              src={s.img}
              alt={s.label}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            {style === s.id && (
              <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-white">
                <Check size={14} strokeWidth={2.5} />
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 p-3 text-left">
              <div className="font-outfit font-bold text-white text-sm uppercase tracking-tight">
                {s.label}
              </div>
              <div className="text-[10px] text-zinc-300/80 truncate">{s.sub}</div>
            </div>
          </button>
        ))}
      </div>

      {scarcityItem?.below_threshold && (
        <div
          className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2.5"
          data-testid="catalog-scarce-notice"
        >
          <Info size={14} className="text-amber-400 mt-0.5 shrink-0" />
          <p
            className="text-xs text-amber-200/90 leading-relaxed"
            dangerouslySetInnerHTML={safeHtml(
              t("simulate.scarcity_warning", {
                count: scarcityItem.total,
                style: styleLabel,
                type:
                  simType === "INTERIOR"
                    ? t("simulate.type_interior")
                    : t("simulate.type_exterior"),
              }),
            )}
          />
        </div>
      )}

      <CatalogPreview
        simType={simType}
        style={style}
        categories={selectedCats}
        limitPerCategory={3}
      />

      {/* Simple user: AI random vs manual category pick toggle */}
      {isSimple && style && (
        <div
          className="mt-4 rounded-md border border-white/10 bg-zinc-900/40 p-4"
          data-testid="simple-mode-toggle"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">
            {t("simulate.simple_mode_title")}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onAiPickRandomChange(true)}
              className={`text-left px-3 py-3 rounded border transition ${
                aiPickRandom
                  ? "border-orange-500 bg-orange-500/10"
                  : "border-white/10 hover:border-white/20 bg-transparent"
              }`}
              data-testid="mode-ai-random"
            >
              <div className="text-sm font-medium text-white">
                {t("simulate.simple_ai_auto")}
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {t("simulate.simple_ai_auto_hint")}
              </div>
            </button>
            <button
              type="button"
              onClick={() => onAiPickRandomChange(false)}
              className={`text-left px-3 py-3 rounded border transition ${
                !aiPickRandom
                  ? "border-orange-500 bg-orange-500/10"
                  : "border-white/10 hover:border-white/20 bg-transparent"
              }`}
              data-testid="mode-manual-pick"
            >
              <div className="text-sm font-medium text-white">
                {t("simulate.simple_manual")}
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {t("simulate.simple_manual_hint")}
              </div>
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 border-t border-white/5 pt-4">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
          {t("simulate.render_mode_title")}
        </div>
        <div className="space-y-2" data-testid="render-mode-picker">
          {modes.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onRenderModeChange(m.id)}
              className={`w-full text-left p-3 rounded-md border-2 transition ${
                renderMode === m.id
                  ? "border-orange-500 bg-orange-500/10"
                  : "border-white/10 bg-zinc-950/40 hover:border-white/30"
              }`}
              data-testid={`render-mode-${m.id.toLowerCase()}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{m.emoji}</span>
                <span className="font-outfit font-bold text-white text-sm">
                  {m.label}
                </span>
                {m.id !== "STANDARD" && (
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-orange-400 font-medium">
                    {t("simulate.render_mode_opt_in")}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-zinc-400 mt-1 leading-snug">
                {m.desc}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
